import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';

const baseStyles = ({ theme, weight }) => css`
  font-family: ${theme.fontStack[weight].name};
  font-weight: ${theme.fontStack[weight].weight};
  margin-bottom: ${theme.spacings.tera};
`;

const sizeStyles = ({ theme, variant, size }) =>
  size &&
  css`
    font-size: ${theme.typography[variant][size].fontSize};
    line-height: ${theme.typography[variant][size].lineHeight};
    letter-spacing: ${theme.typography[variant][size].letterSpacing};
  `;

const centeredStyles = ({ centered }) =>
  centered &&
  css`
    text-align: center;
  `;

const noMarginTopStyles = ({ noMarginTop }) =>
  noMarginTop &&
  css`
    margin-top: 0;
  `;

const noMarginBottomStyles = ({ noMarginBottom }) =>
  noMarginBottom &&
  css`
    margin-bottom: 0;
  `;

const noMarginBothStyles = ({ noMarginBoth }) =>
  noMarginBoth &&
  css`
    margin-top: 0;
    margin-bottom: 0;
  `;

const subHeadingStyles = ({ theme, subHeading }) =>
  subHeading &&
  css`
    color: ${theme.colors.n300};
  `;

const upperStyles = ({ upper }) =>
  upper &&
  css`
    text-transform: uppercase;
  `;

const italicStyles = ({ italic }) =>
  italic &&
  css`
    font-style: italic;
  `;

const designerStyles = ({ theme, designer }) =>
  designer &&
  css`
    font-family: ${theme.fontStack.designer.name};
    font-weight: ${theme.fontStack.designer.weight};
  `;

const shadowStyles = ({ theme, shadow }) =>
  shadow &&
  css`
    text-shadow: -1px 0px 1px ${theme.colors.textShadow};
  `;

const HeadingElement = styled('h2', {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'size',
})`
  ${baseStyles};
  ${sizeStyles};
  ${noMarginTopStyles};
  ${noMarginBottomStyles};
  ${noMarginBothStyles};
  ${subHeadingStyles};
  ${upperStyles};
  ${italicStyles};
  ${designerStyles};
  ${shadowStyles};
  ${centeredStyles};
`;

const Heading = ({ children, as, subHeading, ...restProps }) => (
  <HeadingElement
    {...restProps}
    subHeading={subHeading}
    as={as}
    dangerouslySetInnerHTML={{
      __html: children,
    }}
  />
);

Heading.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  variant: PropTypes.oneOf(['headings', 'subHeadings']),
  size: PropTypes.oneOf(['kilo', 'mega', 'giga', 'tera', 'peta', 'exa', 'zetta', 'yotta', 'deca']),
  weight: PropTypes.string,
  className: PropTypes.string,
  subHeading: PropTypes.string,
  noMarginTop: PropTypes.bool,
  noMarginBottom: PropTypes.bool,
  noMarginBoth: PropTypes.bool,
  centered: PropTypes.bool,
  as: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
};

Heading.defaultProps = {
  variant: 'headings',
  as: 'h2',
  weight: 'bold',
  className: '',
  subHeading: '',
  children: null,
};

export default Heading;
